import { ObjectIdString } from "@r6digital/rapidstor-types";
import Deal from "@r6digital/rapidstor-types/types/Deal";
import { DescriptorCriteria } from "@r6digital/rapidstor-types/types/Descriptor";
import UnitType from "../features/rapidstor/entities/UnitType";

interface RapidStorOptions {
  baseURL: string;
  token?: string;
  corp: string;
  corpToken: string;
  hiddenLocations: Array<string>;
}

export const options: RapidStorOptions = {
  baseURL: import.meta.env.VITE_RAPIDSTOR_URL || "https://prod.rapidstorapp.com/",
  token: import.meta.env.VITE_RAPIDSTOR_API_TOKEN,
  corp: import.meta.env.VITE_RAPIDSTOR_CORP,
  corpToken: import.meta.env.VITE_RAPIDSTOR_CORP_TOKEN,
  hiddenLocations: ["L001", "L005", "L006", "LTEST005"],
};

export type RapidStorResponse<T = void> = {
  operation_message: string;
  operation_result: string;
  results: T;
};

export type RapidStorUnitSortOrder = "size" | "price" | "availability" | "floor" | "none";

export interface RapidStorLocation {
  SiteID: number;
  OwnerID: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [arbitraryOption: string]: any;
  sLocationCode: string;
  iGMTTimeOffset: number;
  sContactName: string | null;
  sEmailAddress: string | null;
  sSitePhone: string | null;
  sWebSiteURL: string | null;
  sSiteName: string;
  friendlyName: string | null;
  sSiteAddr1: string | null;
  sSiteAddr2: string | null;
  sSitePostalCode: string | null;
  sSiteRegion: string | null;
  sSiteCity: string | null;
  sSiteCountry: string | null;
  dcLatitude: string;
  dcLongitude: string;
  deleted: boolean;
  bArchived: string;
  rapidstorEnabled: boolean;
  token: string;
  dCreated: string;
  dUpdated: string;
  rapidstor: {
    allowedCreditCardTypes: {
      [type: string]: boolean;
    };
    allowOriginUrl: string[];
    allowReservation: boolean;
    allowReservationsWhenNoAvailability: boolean;
    assistanceMoving: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [arbitraryOption: string]: any;
    groundFloor: number | string;
    unitListing: {
      useMultilevel: boolean;
      sortOrder: {
        primary: RapidStorUnitSortOrder;
        secondary: RapidStorUnitSortOrder;
        tertiary: RapidStorUnitSortOrder;
      };
    };
    includeReservedUnitsInOccupied: boolean;
    siteLogo: string;
    billingDefault: string;
    sizeSymbol: string;
    areaSizeSymbol: string;
    allowMoveIn: boolean;
    showAreaInsteadOfDimensions: boolean;
    completeMoveinUrl: string;
    completeMoveinURL: string;
    reservationNumberOfDaysInAdvanceLimit: number | string;
    moveinNumberOfDaysInAdvanceLimit: number;
  };
  hours: {
    [arbitraryHours: string]: string;
    officeMondayOpen: string;
    officeTuesdayOpen: string;
    officeWednesdayOpen: string;
    officeThursdayOpen: string;
    officeFridayOpen: string;
    officeSaturdayOpen: string;
    officeSundayOpen: string;
    officeMondayClose: string;
    officeTuesdayClose: string;
    officeWednesdayClose: string;
    officeThursdayClose: string;
    officeFridayClose: string;
    officeSaturdayClose: string;
    officeSundayClose: string;
  };
}

export interface RapidStorUnitType {
  SiteID: number;
  UnitID_FirstAvailable: number;
  UnitID_Representative: number;
  UnitTypeID: number;
  bAlarm: boolean;
  bChargeTax1: boolean;
  bChargeTax2: boolean;
  bClimate: boolean;
  bExcludeFromInsurance: boolean;
  bInside: boolean;
  bMobile: boolean;
  bPower: boolean;
  bPreferredIsPushRate: boolean;
  dcArea: number;
  dcLength: number;
  dcPreferredRate: number;
  dcPushRate: number;
  dcPushRate_NotRounded: number;
  dcReservationFee: number;
  dcStdRate: number;
  dcStdWeeklyRate: number;
  dcTax1Rate_Rent: number;
  dcTax2Rate_Rent: number;
  dcWebRate: number;
  dcWidth: number;
  deals: RapidStorDeal[];
  descriptor: RapidStorDescriptor;
  deleted: boolean;
  hasAvailableUnits: boolean;
  hasComputedAvailable: boolean;
  iDefLeaseNum: number;
  iFloor: number;
  iTotalOccupied: number;
  iTotalReserved: number;
  iTotalUnits: number;
  iTotalVacant: number;
  rates: RapidStorRates;
  sCorpCode: string;
  sLocationCode: string;
  sTypeName: string;
  sUnitName_FirstAvailable: string;
  sUnitName_Representative: string;
  units: unknown[];
  _id: string;
}

export interface RapidStorDescriptor {
  _id: string;
  sLocationCode: string;
  sCorpCode: string;
  name: string;
  description: string;
  picture: string;
  slides: string[];
  defaultInsuranceCoverage?: null | ObjectIdString;
  deals: string[];
  criteria: {
    includes: DescriptorCriteria;
    excludes: DescriptorCriteria;
  };
  enabled: boolean;
  hidden: boolean;
  ordinalPosition: number;
  unitTypes: RapidStorUnitType[];
}

export interface RapidStorDeal {
  _id: string;
  customFormat: unknown;
  descriptions: string[];
  discountPlan: RapidStorDiscountPlan | null;
  fromDate: string | null | Date;
  rates: RapidStorRates;
  title: string;
  toDate: string | null | Date;
}

export interface RapidStorRates {
  discountIsRounded: boolean;
  discountName?: string;
  discountSaved: number | null;
  discountType: string;
  discounted: boolean;
  discountedRate: number | null;
  discountedRateUnrounded: number | null;
  prepaidAmount: number | null;
  prepaidDiscount: boolean;
  prepaidSaved: number | null;
  rate: number;
  rateUnrounded: number;
  tax1: number;
  tax2: number;
  taxInclusive: boolean;
  usingPushRate: boolean;
  usingWebRate: boolean;
}

export interface RapidStorDiscountPlan {
  _id: string;
  ConcessionID: number;
  sPlanName: string;
}

export interface RapidStorUnitList {
  unitTypes: RapidStorUnitType[];
  descriptors: RapidStorDescriptor[];
}

export interface RapidStorInsuranceList {
  insuranceTypes: RapidStorInsurance[];
}

export interface RapidStorInsurance {
  hidden: boolean;
  deleted: boolean;
  maxArea: number;
  // helpfulText: string;
  _id: string;
  InsurCoverageID: number;
  sLocationCode: string;
  SiteID: number;
  changeToken: string;
  changedAt: string;
  dcCoverage: number;
  dcPremium: number;
  dcCoverageDesc: string;
  sProvidor: string;
}

export interface RapidStorPaymentResult {
  error: boolean | null;
  code: string | null;
  message: string | null;
}

export interface RapidStorReservationRequest {
  token?: string;
  locationCode: string;
  payload: ReservationPayload;
}

export interface RapidStorReservation {
  deal: Deal;
  email: string;
  enquiry: boolean;
  firstName: string;
  lastName: string;
  phone: string;
  addr1: string;
  addr2: string;
  city: string;
  state: string;
  country: string;
  mobile: string;
  postalCode: string;
  declaredInsuranceAmt: number | null;
  sUnitName: string;
  tenantId: number | null;
  token: string;
  _id: string;
  unitType: UnitType;
  moveInContext: {
    neededDate: string;
  };
  UnitID: number | null;
  ledgerId: number | null;
}

export interface ReservationPayload {
  deal?: string | null;
  customListing: boolean;
  neededDate?: string; // ISO date string
  timezoneOffset?: number;
  sourceUrl: string;
  tenant: {
    tenantId: number | null;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    addr1: string | null;
    addr2: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
    declaredInsuranceAmt: number | null;
  };
  trackingId?: string;
  unitToUse?: string; // Unit._id
  unitType: string; // UnitType._id
  token: string | null;
  enquiry?: boolean;
}

export interface ReservationPatchRequest {
  locationCode?: string | null;
  token?: string | null;
  payload: {
    tenant: {
      firstName?: string;
      lastName?: string;
      phone?: string;
      mobile?: string;
      countryCode?: string;
      email?: string;
      address?: string;
      address2?: string;
      city?: string;
      postalCode?: string;
      state?: string;
      country?: string;
    };
    reservation: {
      deal?: string | null;
      neededDate?: string;
      insuranceCoverageId?: string;
      insuranceDeclaredAmt?: number;
      termsAndConditionsAgree?: boolean;
      marketingAgree?: boolean;
      unitType?: string;
      unitToUse?: string;
      enquiry?: boolean;
      insuranceType?: string;
    };
    cart: Record<string, number>;
    charges: Record<string, number>;
  };
}

export interface RapidStorMoveInRequest {
  locationCode?: string | null;
  token?: string | null;
  payload: {
    autobill: {
      enabled: boolean;
      usePaymentDetails: boolean;
      type: number;
    };
    card: {
      type: number;
      number: number;
      name: string;
      cvv: number;
      expiryMonth: number;
      expiryYear: number;
    };
  };
}

export interface RapidStorScaMoveInRequest {
  locationCode?: string | null;
  payload: {
    cardHoldersName: string;
    cardNumber: number;
    cardExpiryMonth: number;
    cardExpiryYear: number;
    cardCVV: number;
    cardTypeId: number;
    reservationId: string;
    view: string;
    parentUri?: string;
  };
}

export interface RapidStorCostRetrieveFormatted {
  ChargeDescription: string;
  dcTotal: number;
  ChargeAmount: number;
}

export interface RapidStorCostRetrieve {
  costRetrieveFormatted: RapidStorCostRetrieveFormatted[];
}

export interface RapidStorScaMoveIn {
  redirect: string;
}

export interface RapidStorMoveInResponse {
  ledgerId: string;
  iLeaseNum: string;
  esign: {
    url: string;
    documentId: string;
  };
  additionalAmount: number;
  failedAutobillSetup: boolean;
}

export interface RapidStorChargesMerchRequest {
  locationCode: string;
  tenantId: number;
  ledgerId: number;
  payload: {
    cart: Record<string, number>;
    charges: Record<string, number>;
  };
}

export interface RapidStorTrackEventRequest {
  locationToken: string | undefined;
  corpToken: string | undefined;
  payload: RapidStorTrackEvent;
}

export interface RapidStorTrackEvent {
  trackingId: string;
  url: string;
  referrerUrl: string;
  userAgent: string;
  action: string;
  timestamp: string;
}
